/**
 * Planification FSRS (ADR 0004) : ts-fsrs stable (FSRS-6), avec nos propres étapes
 * d'apprentissage et de réapprentissage (spécification §3, « Étapes »).
 */
import {
  fsrs,
  generatorParameters,
  GenSeedStrategyWithCardId,
  Rating,
  State,
  StrategyMode,
  type Card,
  type FSRSParameters,
  type StepUnit,
  type TLearningStepsStrategy,
} from 'ts-fsrs';
import type { Etapes, ReglagesPaquet } from '../paquets/reglages';

export { State } from 'ts-fsrs';

/** Note de révision : 1 (À revoir), 2 (Difficile), 3 (Bien), 4 (Facile). */
export type Note = 1 | 2 | 3 | 4;
export const NOTES: readonly Note[] = [1, 2, 3, 4];

/**
 * État FSRS d'une carte, avec les noms de champs de ts-fsrs. Donnée dérivée du journal des
 * révisions (ADR 0004).
 */
export type EtatFsrs = Card;

/** Résultat d'une note donnée à une carte. */
export interface Resultat {
  /** État FSRS de la carte après la note. */
  etat: EtatFsrs;
  /** Délai avant le prochain passage, en millisecondes. */
  dureeMs: number;
  /**
   * `true` si la durée est calculée par FSRS (la carte devient ou reste acquise) ; `false` si
   * elle vient des étapes. Décide de la couleur du bouton (spécification §5).
   */
  parFsrs: boolean;
}

export type ReglagesFsrs = Pick<
  ReglagesPaquet,
  | 'objectifMemorisation'
  | 'intervalleMaximumJours'
  | 'etapesApprentissage'
  | 'etapesReapprentissage'
  | 'parametresFsrs'
>;

export interface Planificateur {
  /** Résultat de chacune des quatre notes, pour afficher la durée sur les boutons. */
  apercu(carteId: string, etat: EtatFsrs, maintenant: Date): Record<Note, Resultat>;
  /** Résultat de la note donnée. Identique à `apercu(...)[note]` au même instant. */
  noter(carteId: string, etat: EtatFsrs, maintenant: Date, note: Note): Resultat;
  /** Probabilité de rappel de la carte à cet instant (0 pour une nouvelle carte). */
  probabiliteRappel(etat: EtatFsrs, maintenant: Date): number;
}

const MINUTES_PAR_JOUR = 24 * 60;
const MS_PAR_JOUR = MINUTES_PAR_JOUR * 60 * 1000;

/**
 * Stratégie d'étapes de la spécification, qui remplace celle de ts-fsrs :
 * - 1 (À revoir) : retour au 1er passage, après la durée À revoir de la liste ;
 * - 2 (Difficile) : la carte refait le même passage, après la durée saisie ;
 * - 3 (Bien) : passage suivant après la durée saisie ; au dernier passage, carte acquise ;
 * - 4 (Facile) : carte acquise.
 * « Acquise » = aucune durée renvoyée : ts-fsrs calcule alors l'intervalle.
 *
 * Si la liste a été raccourcie depuis que la carte a commencé ses étapes, la carte reprend
 * au dernier passage existant.
 */
export function strategieEtapes(
  apprentissage: Etapes,
  reapprentissage: Etapes,
): TLearningStepsStrategy {
  return (_parametres, etat, passageCourant) => {
    const etapes =
      etat === State.Relearning || etat === State.Review ? reapprentissage : apprentissage;
    const { aRevoir, passages } = etapes;
    if (passages.length === 0 || aRevoir === null) return {};
    const aRevoirResultat = { scheduled_minutes: aRevoir, next_step: 0 };
    // Carte acquise oubliée : seule la note 1 (À revoir) mène au réapprentissage.
    if (etat === State.Review) return { [Rating.Again]: aRevoirResultat };

    const i = Math.min(Math.max(passageCourant, 0), passages.length - 1);
    const passage = passages[i]!;
    return {
      [Rating.Again]: aRevoirResultat,
      [Rating.Hard]: { scheduled_minutes: passage.difficile, next_step: i },
      ...(passage.bien === undefined
        ? {}
        : { [Rating.Good]: { scheduled_minutes: passage.bien, next_step: i + 1 } }),
    };
  };
}

/**
 * Liste d'étapes au format ts-fsrs. Notre stratégie ne la lit pas, mais ts-fsrs se sert
 * de sa longueur (nombre d'étapes de réapprentissage) pour borner certains paramètres.
 */
function etapesTsFsrs(etapes: Etapes): StepUnit[] {
  if (etapes.aRevoir === null) return [];
  const bien = etapes.passages.slice(0, -1).map((passage) => passage.bien!);
  return [etapes.aRevoir, ...bien].map((minutes): StepUnit => `${minutes}m`);
}

/** Recopie les seuls champs FSRS (ts-fsrs ajoute ou garde des champs en plus). */
function copierEtat(carte: Card): EtatFsrs {
  const etat: EtatFsrs = {
    due: carte.due,
    stability: carte.stability,
    difficulty: carte.difficulty,
    elapsed_days: carte.elapsed_days,
    scheduled_days: carte.scheduled_days,
    learning_steps: carte.learning_steps,
    reps: carte.reps,
    lapses: carte.lapses,
    state: carte.state,
  };
  if (carte.last_review !== undefined) etat.last_review = carte.last_review;
  return etat;
}

/** La graine de l'aléa lit le champ `id` de la carte transmise à ts-fsrs. */
function avecIdentifiant(etat: EtatFsrs, carteId: string): Card {
  const carte: Card & { id: string } = { ...etat, id: carteId };
  return carte;
}

export function creerPlanificateur(reglages: ReglagesFsrs): Planificateur {
  const strategie = strategieEtapes(reglages.etapesApprentissage, reglages.etapesReapprentissage);
  const parametres: FSRSParameters = generatorParameters({
    request_retention: reglages.objectifMemorisation,
    maximum_interval: reglages.intervalleMaximumJours,
    // Petit aléa sur les intervalles (comme Anki) pour éviter que des cartes ajoutées
    // ensemble reviennent toujours ensemble. Graine = identifiant de la carte + nombre de
    // révisions : la durée affichée sur un bouton est celle qui sera appliquée.
    enable_fuzz: true,
    enable_short_term: true,
    learning_steps: etapesTsFsrs(reglages.etapesApprentissage),
    relearning_steps: etapesTsFsrs(reglages.etapesReapprentissage),
    ...(reglages.parametresFsrs === null ? {} : { w: reglages.parametresFsrs }),
  });
  const moteur = fsrs(parametres)
    .useStrategy(StrategyMode.LEARNING_STEPS, strategie)
    .useStrategy(StrategyMode.SEED, GenSeedStrategyWithCardId('id'));

  /**
   * ts-fsrs rend une carte acquise dès qu'une étape dure au moins un jour. Or la carte doit
   * rester en apprentissage (interday) jusqu'au bout de ses passages : on rétablit son état.
   * La date de retour, le passage et l'état mémoire calculés par ts-fsrs restent valables.
   */
  function corrigerEtapeLongue(avant: EtatFsrs, apres: Card, note: Note): Card {
    const etape = strategie(parametres, avant.state, avant.learning_steps)[note];
    if (etape === undefined || etape.scheduled_minutes < MINUTES_PAR_JOUR) return apres;
    const etat =
      avant.state === State.New || avant.state === State.Learning
        ? State.Learning
        : State.Relearning;
    return { ...apres, state: etat };
  }

  function resultat(avant: EtatFsrs, apres: Card, note: Note, maintenant: Date): Resultat {
    const etat = copierEtat(corrigerEtapeLongue(avant, apres, note));
    return {
      etat,
      dureeMs: etat.due.getTime() - maintenant.getTime(),
      parFsrs: etat.state === State.Review,
    };
  }

  return {
    apercu(carteId, etat, maintenant) {
      const apercu = moteur.repeat(avecIdentifiant(etat, carteId), maintenant);
      const parNote = (note: Note) => resultat(etat, apercu[note].card, note, maintenant);
      return { 1: parNote(1), 2: parNote(2), 3: parNote(3), 4: parNote(4) };
    },
    noter(carteId, etat, maintenant, note) {
      const { card } = moteur.next(avecIdentifiant(etat, carteId), maintenant, note);
      return resultat(etat, card, note, maintenant);
    },
    probabiliteRappel(etat, maintenant) {
      if (etat.state === State.New || etat.last_review === undefined) return 0;
      // Temps écoulé en jours non arrondis (ts-fsrs arrondit au jour inférieur dans
      // get_retrievability) : départage les cartes revues le même jour.
      const jours = Math.max(0, (maintenant.getTime() - etat.last_review.getTime()) / MS_PAR_JOUR);
      return moteur.forgetting_curve(jours, etat.stability);
    },
  };
}

/** État FSRS d'une carte jamais vue. */
export function etatNouvelleCarte(maintenant: Date): EtatFsrs {
  return {
    due: maintenant,
    stability: 0,
    difficulty: 0,
    elapsed_days: 0,
    scheduled_days: 0,
    learning_steps: 0,
    reps: 0,
    lapses: 0,
    state: State.New,
  };
}
